// Écran de connexion. Volontairement SANS route : il remplace l'application
// entière tant qu'il n'y a pas de session, plutôt que de vivre à `#/login`.
//
// POURQUOI — une route de login se contourne en tapant une autre adresse, ce
// qui donne une interface à moitié montée avant que les appels d'API ne
// commencent à rendre 401. En le posant au-dessus du routeur, l'état « pas
// connecté » n'a qu'un seul rendu possible.

import { mesurerEcran } from '../lib/mesure'
import { useEffect, useRef, useState } from 'react'
import { LogIn } from 'lucide-react'
import { Radar as IconeRadar } from 'lucide-react'
import { ChampMotDePasse, PanneauInscription } from './Inscription'
import { ApiError, get, post } from '../lib/api'
import { parseQuery } from '../lib/router'
import { useLang, useSetLang } from '../lib/lang'
import { SelecteurLangue } from '../components/selecteurLangue'
import { useT } from '../lib/i18n'
import type { FormuleService } from '../lib/formule'

/** La raison d'un retour de Google raté (`/#/?google=…`, web/google.ts). */
const MESSAGES_GOOGLE = {
  sans_compte: 'login.google.sans_compte',
  expire: 'login.google.expire',
  annule: 'login.google.annule',
  erreur: 'login.google.erreur',
  ferme: 'login.google.ferme',
  refuse: 'login.google.refuse',
} as const

export function Login({ formule, onConnecte }: { formule: FormuleService; onConnecte: () => void }) {
  const t = useT()
  const lang = useLang()
  const setLang = useSetLang()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [voirMdp, setVoirMdp] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const [envoi, setEnvoi] = useState(false)
  // Ce que le serveur propose (#93, #182) : Google, l'inscription par Google
  // ou par adresse, et la clé Turnstile. Un retour de Google raté revient ici
  // avec sa raison.
  const [methodes, setMethodes] = useState<Methodes>({ google: false })
  const [mode, setMode] = useState<'connexion' | 'inscription'>(() =>
    parseQuery(location.hash).get('mode') === 'inscription' ? 'inscription' : 'connexion'
  )
  useEffect(() => {
    if (formule === 'admin') return
    get<Methodes>('/api/auth/methodes').then(setMethodes, () => {})
  }, [formule])
  const retourGoogle = parseQuery(location.hash).get('google')
  const radar = formule === 'radar'
  // L'onglet choisi est un écran à part pour la mesure (issue #193) : c'est le
  // premier pas de l'entonnoir d'inscription. `mesurerEcran` ne part qu'en Radar.
  useEffect(() => {
    mesurerEcran(mode)
  }, [mode])
  const inscriptionPossible = radar && Boolean(methodes.email || methodes.inscription)

  async function soumettre(e: React.FormEvent) {
    e.preventDefault()
    setErreur(null)
    setEnvoi(true)
    try {
      await post('/api/auth/login', { email, password })
      onConnecte()
    } catch (err) {
      setErreur(err instanceof ApiError ? err.message : t('login.error.generic'))
    } finally {
      setEnvoi(false)
    }
  }

  const sousTitre = radar ? t('login.radar.accroche') : formule === 'admin' ? t('login.subtitle.admin') : t('login.subtitle.console')

  return (
    <div className="loginwrap">
      {/* Pas encore de compte à qui attacher un choix : la bascule reste
          purement locale ici (cf. useLangEtat dans App.tsx, qui la réécrit
          sur le compte après une connexion réussie si elle diffère de la
          valeur déjà enregistrée). */}
      <SelecteurLangue className="logintoggle" value={lang} onChange={setLang} />

      <div className="card logincard">
        <div className="loginmarque">
          <span className="logo" aria-hidden="true"><IconeRadar size={18} /></span>
          <h1 className="loginh">{radar ? 'Radar' : 'AI Job Fit'}</h1>
        </div>
        <p className="loginsub">{sousTitre}</p>

        {/* Deux onglets quand on peut s'inscrire (#182) : se connecter ou créer
            un compte, sur la même carte, sans changer de page. */}
        {inscriptionPossible && (
          <div className="segstatus loginmodes" role="tablist" aria-label={t('login.modes')}>
            {(['connexion', 'inscription'] as const).map((m) => (
              <button
                key={m}
                type="button"
                role="tab"
                aria-selected={mode === m}
                className={`seg${mode === m ? ' on' : ''}`}
                onClick={() => { setMode(m); setErreur(null) }}
              >
                {m === 'connexion' ? t('login.submit') : t('inscription.titre')}
              </button>
            ))}
          </div>
        )}

        {/* Google d'abord : la voie la plus courte, sans mot de passe à
            retenir. En inscription, il crée le compte (#182). */}
        {methodes.google && (mode === 'connexion' || methodes.inscription) && (
          <>
            <a className="btn-secondary logingoogle" href="/api/auth/google">
              <LogoGoogle />
              {t('login.google.bouton')}
            </a>
            <div className="loginou" aria-hidden="true"><span>{t('login.ou')}</span></div>
          </>
        )}

        {!erreur && retourGoogle && retourGoogle in MESSAGES_GOOGLE && (
          <p className="loginerr" role="alert">{t(MESSAGES_GOOGLE[retourGoogle as keyof typeof MESSAGES_GOOGLE])}</p>
        )}

        {mode === 'inscription' && inscriptionPossible ? (
          methodes.email ? (
            <PanneauInscription turnstile={methodes.turnstile ?? null} />
          ) : (
            <p className="field-help">{t('login.google.inscription')}</p>
          )
        ) : (
          <form className="loginform" onSubmit={soumettre}>
            <label className="field-label" htmlFor="login-email">{t('login.email')}</label>
            <input
              id="login-email"
              className="input-field"
              type="email"
              autoComplete="username"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <label className="field-label" htmlFor="login-password">{t('login.password')}</label>
            <ChampMotDePasse id="login-password" autoComplete="current-password" valeur={password} onChange={setPassword} voir={voirMdp} onVoir={setVoirMdp} />

            {/* `role="alert"` pour que l'échec soit annoncé : sans lui, un lecteur
                d'écran ne signale rien et l'utilisateur croit que rien ne s'est
                passé. */}
            {erreur && <p className="loginerr" role="alert">{erreur}</p>}

            <button className="btn-primary" type="submit" disabled={envoi}>
              <LogIn size={16} />
              {envoi ? t('login.submitting') : t('login.submit')}
            </button>
            {radar && <div className="logincentre"><Oubli email={email} /></div>}
          </form>
        )}
      </div>
    </div>
  )
}

interface Methodes {
  google: boolean
  /** Google crée aussi le compte (#182). */
  inscription?: boolean
  /** Le formulaire d'inscription par adresse existe (#182). */
  email?: boolean
  turnstile?: string | null
}

/** Le logo de Google, tel que Google demande de l'afficher sur son bouton. */
function LogoGoogle() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.6 5.4 2.7 13.2l7.9 6.2C12.5 13.6 17.8 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.1 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.4c-.5 2.9-2.2 5.3-4.6 7l7.4 5.7c4.3-4 6.9-9.9 6.9-17.2z" />
      <path fill="#FBBC05" d="M10.6 28.6c-.5-1.4-.8-3-.8-4.6s.3-3.2.8-4.6l-7.9-6.2C1 16.6 0 20.2 0 24s1 7.4 2.7 10.8l7.9-6.2z" />
      <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.4-5.7c-2.1 1.4-4.8 2.3-8.5 2.3-6.2 0-11.5-4.1-13.4-9.9l-7.9 6.2C6.6 42.6 14.6 48 24 48z" />
    </svg>
  )
}

/**
 * « Mot de passe oublié ? » (issue #96, Radar seulement) : un lien de
 * connexion part à l'adresse, et la réponse ne dit jamais si elle a un compte.
 */
function Oubli({ email }: { email: string }) {
  const t = useT()
  const [etat, setEtat] = useState<'ferme' | 'ouvert' | 'parti'>('ferme')
  const [adresse, setAdresse] = useState(email)
  const [erreur, setErreur] = useState<string | null>(null)
  if (etat === 'ferme') {
    return (
      <button type="button" className="linkbtn" onClick={() => { setAdresse(email); setEtat('ouvert') }}>
        {t('login.oubli.lien')}
      </button>
    )
  }
  if (etat === 'parti') return <p className="field-help" role="status">{t('login.oubli.parti')}</p>
  return (
    <div className="logincard-oubli">
      <label className="field-label" htmlFor="oubli-email">{t('login.oubli.titre')}</label>
      <input id="oubli-email" className="input-field" type="email" value={adresse} onChange={(e) => setAdresse(e.target.value)} />
      {erreur && <p className="loginerr" role="alert">{erreur}</p>}
      <button
        type="button"
        className="btn-secondary"
        disabled={!adresse.includes('@')}
        onClick={async () => {
          setErreur(null)
          try {
            await post('/api/auth/oubli', { email: adresse })
            setEtat('parti')
          } catch (err) {
            setErreur(err instanceof ApiError ? err.message : t('login.error.generic'))
          }
        }}
      >
        {t('login.oubli.envoyer')}
      </button>
    </div>
  )
}

/** La page où mène le lien du mot de passe oublié : elle le consomme une fois,
 *  et l'écran de changement de mot de passe prend la suite. */
export function LienConnexion({ token, onConnecte }: { token: string; onConnecte: () => void }) {
  const t = useT()
  const [erreur, setErreur] = useState<string | null>(null)
  // Une seule consommation : le lien ne vaut qu'une fois, et React rejoue les
  // effets en développement.
  const fait = useRef(false)
  useEffect(() => {
    if (fait.current) return
    fait.current = true
    post('/api/auth/oubli/ouvrir', { token }).then(onConnecte, (err) =>
      setErreur(err instanceof ApiError ? err.message : t('login.error.generic'))
    )
  }, [token, onConnecte, t])
  return (
    <div className="loginwrap">
      <div className="card logincard">
        <h1 className="loginh">AI Job Fit</h1>
        {erreur ? (
          <>
            <p className="loginerr" role="alert">{erreur}</p>
            <a className="btn-secondary" href="#/">{t('login.submit')}</a>
          </>
        ) : (
          <p className="loginsub">{t('login.submitting')}</p>
        )}
      </div>
    </div>
  )
}
